import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { z } from 'zod';

import TrashIcon from '@/assets/svg/trashcan.svg?react';
import { Button } from '@/components/buttons';
import { LogicButton, type LogicButtonProps } from '@/components/buttons/LogicButton';
import { encodeFilterFieldName, FilterField, useFilterForm } from '@/components/filters';
import { FieldError } from '@/components/Form/fields';
import { TextInput } from '@/components/Form/inputs';
import type { FilterOperatorMulti, SolQueryFilterOperator } from '@/components/InstantSearch';
import { Text } from '@/components/typography';
import type { FilterComponentProps } from '@/providers/TableState';

const MAX_URL_FILTERS = 10;

type BooleanOperator = 'OR' | 'AND';

const defaultUrlValue = (): FilterOperatorMulti => ({
  operator: 'contains',
  operand: [],
  booleanOperator: 'OR' as BooleanOperator,
});

const URLFilter = ({ name, label, infoPopover }: FilterComponentProps) => {
  const form = useFilterForm();
  const fieldName = encodeFilterFieldName(name);

  return (
    <form.AppField
      name={fieldName}
      validators={{
        onSubmit: z.nullish(
          z.object({
            operator: z.custom<SolQueryFilterOperator>(),
            operand: z.array(
              z
                .string()
                .trim()
                .min(1, t`URL fragment cannot be empty`),
            ),
            booleanOperator: z.optional(z.enum(['OR', 'AND'])),
          }),
        ),
      }}
    >
      {(field) => {
        const raw = field.state.value as FilterOperatorMulti | null | undefined;
        const value = raw ?? defaultUrlValue();
        const operand = (value?.operand as string[]) ?? [];
        const booleanOperator = (value?.booleanOperator as BooleanOperator) ?? 'OR';

        const update = (patch: Partial<FilterOperatorMulti>) => {
          field.handleChange({ ...value, ...patch } as FilterOperatorMulti);
        };

        const handleAdd = () => {
          if (!raw) {
            field.handleChange({
              operator: 'contains',
              operand: [''],
              booleanOperator: 'OR',
            } as FilterOperatorMulti);
          } else {
            update({ operand: [...operand, ''] });
          }
        };

        const handleRemove = (index: number) => {
          const next = operand.filter((_, i) => i !== index);
          if (next.length === 0) {
            field.handleChange(null);
          } else {
            update({ operand: next });
          }
        };

        const handleChangeUrl = (index: number, text: string) => {
          update({ operand: operand.map((v, i) => (i === index ? text : v)) });
        };

        const handleBooleanOperatorChange: LogicButtonProps['onChange'] = (op) => {
          update({ booleanOperator: op as BooleanOperator });
        };

        return (
          <FilterField data-testid="url-filter" label={label} infoPopover={infoPopover}>
            <div className="flex flex-col items-start gap-2">
              {operand.length === 0 && (
                <Text variant="body1" color="grey" italic weight="extra-light">
                  <Trans>no URL filters added</Trans>
                </Text>
              )}
              {operand.map((url, index) => (
                <div className="flex w-full flex-col gap-2" key={index}>
                  {index !== 0 && (
                    <LogicButton
                      className="self-start"
                      value={booleanOperator}
                      onChange={handleBooleanOperatorChange}
                    />
                  )}
                  <div className="flex gap-2">
                    <TextInput
                      className="flex-1"
                      placeholder={t`Type a URL fragment...`}
                      value={url}
                      onValueChange={(text) => handleChangeUrl(index, text)}
                    />
                    <Button
                      aria-label={t`Remove URL filter`}
                      variant="ghost"
                      shape="square"
                      danger
                      className="mt-1"
                      size="icon"
                      onClick={() => handleRemove(index)}
                    >
                      <TrashIcon />
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                className="self-start"
                variant="primary"
                disabled={operand.length >= MAX_URL_FILTERS}
                onClick={handleAdd}
              >
                <Trans>Add +</Trans>
              </Button>
              {field.state.meta.errors.length > 0 && (
                <FieldError errors={field.state.meta.errors} />
              )}
            </div>
          </FilterField>
        );
      }}
    </form.AppField>
  );
};

export default URLFilter;
